import { useState } from "react";
import { AirplaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { autopilotAPI } from "../lib/api";
import { formatCurrency } from "../lib/formatCurrency";
import { isAutopilotExpense } from "./AutopilotBadge";

const isSingleOverhead = (exp) => {
  const rows = exp.splits?.length ? exp.splits : [{ type: exp.expense_type, gl_account_id: exp.gl_account_id }];
  if (rows.some((r) => (r.type || "").toLowerCase() === "job")) return false;
  const ids = new Set(rows.map((r) => r.gl_account_id || ""));
  return ids.size === 1 && !ids.has("");
};

export const canMakeAutopilotRule = (exp) =>
  !!exp && exp.status === "approved" && !!exp.vendor_qbo_id && !exp.is_simulated && exp.amount > 0 && !isAutopilotExpense(exp) && isSingleOverhead(exp);

export const MakeAutopilotRuleButton = ({ exp, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [max, setMax] = useState("");
  const [saving, setSaving] = useState(false);
  if (!canMakeAutopilotRule(exp)) return null;

  const openDialog = (e) => {
    e?.stopPropagation();
    setMax(String(Math.ceil(exp.amount * 1.25)));
    setOpen(true);
  };

  const save = async () => {
    const value = parseFloat(max);
    if (!(value > 0)) return toast.error("Enter a max amount above $0");
    setSaving(true);
    try {
      const { data } = await autopilotAPI.fromExpense(exp.id, value);
      toast.success(`${data.updated ? "Updated" : "Created"} Autopilot rule for ${data.rule.vendor_name}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create the rule");
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" variant={compact ? "ghost" : "outline"} onClick={openDialog}
        data-testid={`make-autopilot-rule-btn-${exp.id}`}
        title="Auto-code future swipes from this vendor to the same overhead account"
        className={compact ? "h-7 px-2 text-sky-700 hover:bg-sky-50 text-xs gap-1" : "rounded-sm text-xs gap-1"}>
        <AirplaneTilt size={12} weight="bold" /> {compact ? "Autopilot" : "Make this an autopilot rule"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="make-autopilot-rule-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{exp.vendor_has_autopilot_rule ? "Update Autopilot rule" : "New Autopilot rule"}</DialogTitle>
            <DialogDescription className="text-xs">
              Future swipes at <strong>{exp.vendor}</strong> up to the max below get coded to{" "}
              <strong>{exp.splits?.[0]?.gl_account_name || exp.gl_account_name || "this overhead account"}</strong>, approved and pushed automatically.
              {exp.vendor_has_autopilot_rule && <span className="block mt-1 text-amber-700" data-testid="make-autopilot-rule-existing-note">{exp.vendor} already has a rule — saving replaces its account and max.</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Max per swipe (this one was {formatCurrency(exp.amount)})</Label>
            <Input type="number" min="0" step="0.01" value={max} onChange={(e) => setMax(e.target.value)}
              className="rounded-sm font-mono" data-testid="make-autopilot-rule-max-input" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" disabled={saving}
              onClick={save} data-testid="make-autopilot-rule-save-btn">
              {saving ? "Saving…" : exp.vendor_has_autopilot_rule ? "Update rule" : "Create rule"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
